import { displaySides, displayRecord } from "./comparison-presentation.js";
import type {
  ComparisonDetail,
  ComparisonObservation,
  ComparisonRelation,
} from "../../../shared/contracts/product-pages.js";

export interface AnalysisProps {
  detail: ComparisonDetail;
  onOpenRoundId?(id: string): void;
}
export function ComparisonNames({ detail }: AnalysisProps): React.JSX.Element {
  return (
    <div className="rc-stat-names">
      {displaySides(detail.relation).map((side) => (
        <span key={side} data-side={side}>
          {detail.relation[side].name}
        </span>
      ))}
    </div>
  );
}
export function ObservationCard({
  title,
  observation: item,
  detail,
}: AnalysisProps & {
  title: string;
  observation: ComparisonObservation;
}): React.JSX.Element {
  const sides = displaySides(detail.relation);
  const percentage = item.metric === "gir" || item.metric === "fairways";
  const values = sides.map((side) => item[`${side}Mean`]);
  return (
    <article className="rc-stat-row">
      <header>
        <h3>{title}</h3>
        <small>
          {item.n
            ? `${item.n} ${item.unit === "paired-hole" ? "holes" : "rounds"}`
            : "No data"}
        </small>
      </header>
      <div className="rc-stat-values">
        {sides.map((side, index) => {
          const value = values[index],
            other = values[1 - index];
          const leading =
            value != null &&
            other != null &&
            (percentage ? value > other : value < other);
          return (
            <strong
              key={side}
              data-leading={leading}
              aria-label={`${detail.relation[side].name}: ${statValue(value, percentage)}`}
            >
              {statValue(value, percentage)}
            </strong>
          );
        })}
      </div>
      <div className="rc-stat-bars" aria-hidden="true">
        {sides.map((side) => (
          <span
            key={side}
            data-side={side}
            style={{
              width: `${item.n ? ((item[`${side}Wins`] + item.ties / 2) / item.n) * 100 : 50}%`,
            }}
          />
        ))}
      </div>
      {item.n ? (
        <footer>
          <span>
            {percentage
              ? "Hit rate"
              : item.metric === "putts"
                ? "Avg. putts"
                : "Avg. strokes"}
          </span>
          <span>
            {sides.map((side) => item[`${side}Wins`]).join("–")} ahead ·{" "}
            {item.ties} tied
          </span>
        </footer>
      ) : null}
    </article>
  );
}
function statValue(
  value: number | null | undefined,
  percentage: boolean,
): string {
  return value == null
    ? "—"
    : percentage
      ? `${Math.round(value * 100)}%`
      : value.toFixed(1);
}
export function ComparisonRecordBoard({
  relation,
  record = relation.record,
  sharedRounds = relation.sharedRoundCount ?? relation.record.roundCount,
}: {
  relation: ComparisonRelation;
  record?: ComparisonRelation["record"];
  sharedRounds?: number;
}): React.JSX.Element {
  const displayed = displayRecord(relation, record, true);
  return (
    <div
      className="rc-record"
      aria-label={`${displayed[0]!.name} ${displayed[0]!.wins} wins, ${displayed[1]!.name} ${displayed[1]!.wins} wins, ${record.ties} tied`}
    >
      <div>
        <span>{displayed[0]!.name}</span>
        <b>{displayed[0]!.wins}</b>
        <small>WINS</small>
      </div>
      <div className="rc-record-middle">
        <span>—</span>
        <small>{record.ties} tied</small>
      </div>
      <div>
        <span>{displayed[1]!.name}</span>
        <b>{displayed[1]!.wins}</b>
        <small>WINS</small>
      </div>
      <p>
        {sharedRounds} rounds · {record.contestCount} results
        {record.challengeCount ? ` · ${record.challengeCount} challenges` : ""}
        {relation.testRounds?.length
          ? ` · ${relation.testRounds.length} starting wins`
          : ""}
      </p>
    </div>
  );
}
